import { XIcon } from "@heroicons/react/outline";
import moment from "moment";
import { TbCalendarPlus } from "react-icons/tb";

import Button, { buttonTheme } from "@components/button/button";
import { InputTheme } from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

import { MeetingDialogFormType } from "../meeting-dialog";
import MeetingRecurrence from "./recurrence";

const MeetingDates = ({
  isReadOnly,
  form,
  onChangeForm,
}: {
  isReadOnly: boolean;
  form: MeetingDialogFormType;
  onChangeForm: (form: MeetingDialogFormType) => void;
}) => {
  const handleChangeStartDatetime = (date?: Date | null) => {
    if (!date) {
      return;
    }
    const currentMinuteDifference = moment(form.endDatetime).diff(
      form.startDatetime,
      "minutes"
    );
    const endDatetime = moment(date).isSameOrAfter(form.endDatetime, "minutes")
      ? moment(date).add(currentMinuteDifference, "minute").format()
      : form.endDatetime;
    onChangeForm({
      ...form,
      startDatetime: moment(date).format(),
      endDatetime,
    });
  };

  const handleChangeEndDatetime = (date?: Date | null) => {
    if (!date) {
      return;
    }
    const currentMinuteDifference = moment(form.endDatetime).diff(
      form.startDatetime,
      "minutes"
    );
    const startDatetime = moment(date).isSameOrBefore(
      form.startDatetime,
      "minutes"
    )
      ? moment(date).subtract(currentMinuteDifference, "minutes").format()
      : form.startDatetime;
    onChangeForm({
      ...form,
      startDatetime,
      endDatetime: moment(date).format(),
    });
  };

  const handleChangeMeetingRecurrence = (meetingFrequency: any) => {
    onChangeForm({ ...form, recurrence: meetingFrequency });
  };

  const handleClickSetDate = () => {
    onChangeForm({
      ...form,
      startDatetime: moment()
        .hour(moment().hour())
        .add(1, "hour")
        .startOf("hour")
        .format(),
      endDatetime: moment()
        .hour(moment().hour())
        .add(1, "hour")
        .startOf("hour")
        .add(30, "minutes")
        .format(),
    });
  };

  const handleClickClearDate = () => {
    onChangeForm({
      ...form,
      startDatetime: undefined,
      endDatetime: undefined,
      recurrence: null,
    });
  };

  return !form.startDatetime && !form.endDatetime ? (
    <div className="flex flex-col gap-1">
      <Button
        onClick={handleClickSetDate}
        theme={buttonTheme.text}
        leftNegativeMargin
      >
        <TbCalendarPlus className="h-4 w-4" />
        Add date
      </Button>
    </div>
  ) : (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between w-full">
        {moment(form.startDatetime).isSame(form.endDatetime, "day") ? (
          <div className="flex-1 flex items-center -ml-3">
            <InputDatePicker
              date={form.startDatetime}
              onChangeDate={handleChangeStartDatetime}
              dateFormat="EEE, MMM d, yyyy"
              ariaLabelledBy="Meeting dialog start date input"
              disabled={isReadOnly}
              theme={InputTheme.text}
              placeholderText="Start date"
              isClearable={false}
              buttonProps={{}}
            />
            <div className="ml-3 flex items-center">
              <InputDatePicker
                date={form.startDatetime}
                onChangeDate={handleChangeStartDatetime}
                showTimeSelect
                showTimeSelectOnly
                dateFormat="h:mma"
                showIcon={false}
                isClearable={false}
                disabled={isReadOnly}
                theme={InputTheme.text}
                placeholderText="Time"
                ariaLabelledBy="Meeting dialog start time input"
                buttonProps={{}}
              />
              <span className="text-sm mx-0.5">-</span>
              <InputDatePicker
                date={form.endDatetime}
                onChangeDate={handleChangeEndDatetime}
                showTimeSelect
                showTimeSelectOnly
                dateFormat="h:mma"
                showIcon={false}
                isClearable={false}
                disabled={isReadOnly}
                theme={InputTheme.text}
                placeholderText="Time"
                ariaLabelledBy="Meeting dialog end time input"
                buttonProps={{}}
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center -ml-3">
            <InputDatePicker
              date={form.startDatetime}
              onChangeDate={handleChangeStartDatetime}
              dateFormat="MMM d, yyyy, h:mma"
              showTimeSelect
              ariaLabelledBy="Meeting dialog start date input"
              disabled={isReadOnly}
              theme={InputTheme.text}
              placeholderText="Start date"
              buttonProps={{}}
            />
            <TopicflowIcon
              type="arrowRight"
              className="h-3 w-3 text-gray-400"
            />
            <InputDatePicker
              date={form.endDatetime}
              onChangeDate={handleChangeEndDatetime}
              showTimeSelect
              dateFormat="MMM d, yyyy, h:mma"
              disabled={isReadOnly}
              theme={InputTheme.text}
              showIcon={false}
              placeholderText="End date"
              ariaLabelledBy="Meeting dialog end date input"
              buttonProps={{}}
            />
          </div>
        )}
        {!isReadOnly && (
          <Button
            icon={XIcon}
            tooltip="Remove meeting date"
            theme={buttonTheme.iconGray}
            onClick={handleClickClearDate}
          />
        )}
      </div>
      <MeetingRecurrence
        isReadOnly={isReadOnly}
        buttonProps={{
          "aria-label": "Meeting dialog recurrence input",
          leftNegativeMargin: true,
          theme: buttonTheme.text,
        }}
        formStartDatetime={form.startDatetime}
        formMeetingFrequency={form.recurrence}
        onChangeMeetingFrequency={handleChangeMeetingRecurrence}
      />
    </div>
  );
};

export default MeetingDates;
